<template>
  <div class="modal modal-pending-approval" :class="{ 'show': showModal }">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Pending Commissions</h5>
          <button type="button" class="close" @click="closeModal">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="text-left">
            <p>
              To accept the commissions, please finish the pending commissions.
              There are <strong>{{ messages.unfinished_commissionables_count }} pending approval</strong>.
            </p>
            <p><b>Accountant:</b> {{ messages.to_check_count }} pending</p>
            <p><b>Director:</b> {{ messages.pending_director_count }} pending</p>
            <p><b>Consultant:</b> {{ messages.pending_count }} pending</p>
            <p><b>Manager:</b> {{ messages.manager_check_count }} pending</p>
            <p><b>Declined:</b> {{ messages.declined_count }} pending</p>
          </div>

          <div class="pending-approval-students">
            <table class="table table-bordered mb-0">
              <thead>
              <tr>
                <th scope="col">Student</th>
                <th scope="col">School</th>
                <th scope="col">Course</th>
                <th scope="col">Type</th>
                <th scope="col" class="text-nowrap">Tuition Amount</th>
                <th scope="col" class="text-center">Actions</th>
              </tr>
              </thead>
              <tbody>
              <template v-for="(student) in students">
                <tr :id="'student-' + student.commissionable_id">
                  <td class="student-name" :title="student.student_full_name">
                    <a :href="student.student_url">{{ truncatedName(student.student_full_name) }}</a>
                  </td>
                  <td :title="student.school_name">{{ truncatedName(student.school_name) }}</td>
                  <td :title="student.course_name">{{ truncatedName(student.course_name) }}</td>
                  <td>{{ truncatedName(transformedText(student.type === 'closed' ? '-' : student.type), 10) }}</td>
                  <td>{{ formattedNumbers(student.amount) }}</td>
                  <td class="text-center">
                    <button
                      :class="`btn btn-sm btn-approval ${getButtonClass(student)}`"
                      :disabled="isButtonDisabled(student)"
                      @click="toggleApprovalForm(student)"
                    >
                      {{ getButtonText(student) }}
                    </button>
                  </td>
                </tr>
                <tr v-if="selectedCommissionables.includes(student.commissionable_id)" :id="'form-' + student.commissionable_id">
                  <td colspan="6">
                    <form @submit.prevent="submitApproval(student.commissionable_id)">
                      <div class="form-group">
                        <input v-model="approvalData[student.commissionable_id].answer" type="radio" :id="'approve' + student.commissionable_id" value="approve" required>
                        <label :for="'approve' + student.commissionable_id">Approve</label>
                        <input v-model="approvalData[student.commissionable_id].answer" type="radio" :id="'decline' + student.commissionable_id" value="decline" required>
                        <label :for="'decline' + student.commissionable_id">Decline</label>

                        <p v-if="!approvalData[student.commissionable_id].answer" class="text-danger">
                          Please select an option.
                        </p>
                      </div>
                      <div class="form-group" v-if="approvalData[student.commissionable_id].answer === 'decline'">
                        <label :for="'reason' + student.commissionable_id">Reason:</label>
                        <textarea v-model="approvalData[student.commissionable_id].reason" :id="'reason' + student.commissionable_id" class="form-control" rows="3"></textarea>
                      </div>
                      <button type="submit" class="btn btn-primary">Submit</button>
                      <button type="button" class="btn btn-secondary" @click="cancelApproval(student.commissionable_id)">Cancel</button>
                    </form>
                  </td>
                </tr>
              </template>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" :disabled="isAcceptingCommission" @click="$emit('accept', true)">
            {{ isAcceptingCommission ? 'Processing...' : 'Accept Commission' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatCurrency, transformText, truncateText } from "../../../utils/helpers";
import { processApproval, processPossibleCommission } from "../../../services/api/commissions";

import '../../../../sass/commissions/pending-approval-modal.scss';

export default {
  name: 'CommissionApprovalModal',
  props: {
    userId: {
      type: Number,
      default: 0,
    },
    messages: {
      type: [Array, Object],
      default: () => [],
    },
    students: {
      type: [Array, Object],
      default: () => [],
    },
    isAcceptingCommission: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoadingApproval: false,
      showModal: false,
      selectedCommissionables: [],
      approvedStudents: [],
      declinedStudents: [],
      approvalData: {},
      processingId: null
    };
  },
  methods: {
    closeModal() {
      this.showModal = false;
      this.selectedCommissionables = [];
      this.approvalData = {}
    },
    formattedNumbers(number) {
      return formatCurrency(number);
    },
    truncatedName(name, limit = 20) {
      return truncateText(name, limit);
    },
    transformedText(text) {
      return transformText(text);
    },
    getButtonClass(student) {
      if (student.type === 'closed' && this.approvedStudents.includes(student.commissionable_id)) {
        return 'btn-secondary';
      }
      if (student.type === 'closed') {
        return 'btn-warning';
      }
      if (this.declinedStudents.includes(student.commissionable_id)) {
        return 'btn-danger';
      }
      if (this.approvedStudents.includes(student.commissionable_id)) {
        return 'btn-secondary';
      }
      return 'btn-approve';
    },
    getButtonText(student) {
      if (student.type === 'closed' && this.approvedStudents.includes(student.commissionable_id)) {
        return 'Commission sent';
      }
      if (student.type === 'closed') {
        return 'Send to commission';
      }
      if (this.declinedStudents.includes(student.commissionable_id)) {
        return 'Declined';
      }
      if (this.approvedStudents.includes(student.commissionable_id)) {
        return 'Approved';
      }
      return this.processingId === student.commissionable_id ? 'Processing..' : 'Approve';
    },
    isButtonDisabled(student) {
      return this.processingId === student.commissionable_id || this.approvedStudents.includes(student.commissionable_id) || this.declinedStudents.includes(student.commissionable_id);
    },
    toggleApprovalForm(student) {
      if (this.approvedStudents.includes(student.commissionable_id) || this.declinedStudents.includes(student.commissionable_id)) {
        return;
      }

      if (student.type === 'closed') {
        this.submitApproval(student.commissionable_id);
        return;
      }

      const index = this.selectedCommissionables.indexOf(student.commissionable_id);

      if (index > -1) {
        this.selectedCommissionables.splice(index, 1);
        delete this.approvalData[student.commissionable_id];

        if (this.processingId === student.commissionable_id) {
          this.processingId = null;
        }
      } else {
        this.selectedCommissionables.push(student.commissionable_id);
        this.$set(this.approvalData, student.commissionable_id, { answer: '', reason: '' });
      }
    },
    cancelApproval(commissionable_id) {
      const index = this.selectedCommissionables.indexOf(commissionable_id);

      if (index > -1) {
        this.selectedCommissionables.splice(index, 1);
        delete this.approvalData[commissionable_id];
      }
    },
    async submitApproval(commissionable_id) {
      const student = this.students.find(s => s.commissionable_id === commissionable_id);
      const isPossibleType = student && student.type === 'closed';

      try {
        this.isLoadingApproval = true;
        this.processingId = commissionable_id;

        let response;
        if (isPossibleType) {
          response = await processPossibleCommission(this.userId, commissionable_id);
        } else {
          response = await processApproval(commissionable_id, this.approvalData[commissionable_id]);
        }

        if (response.status === 200 && response.data.status === 'success') {
          if (!isPossibleType) {
            const answer = this.approvalData[commissionable_id].answer;

            if (answer === 'approve') {
              this.approvedStudents.push(commissionable_id);
            } else if (answer === 'decline') {
              this.declinedStudents.push(commissionable_id);
            }

            const index = this.selectedCommissionables.indexOf(commissionable_id);
            if (index > -1) {
              this.selectedCommissionables.splice(index, 1);
              delete this.approvalData[commissionable_id];
            }
          } else {
            this.approvedStudents.push(commissionable_id);
          }

          new Noty({
            type: 'success',
            text: response.data.message,
          }).show();
        }
      } catch (error) {
        console.error('Error in approval process:', error);

        new Noty({
          type: "error",
          text: 'Failed to process the approval. Please try again later.',
        }).show();
      } finally {
        this.isLoadingApproval = false;
        this.processingId = null;
      }
    }
  }
};
</script>